import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import VisionModeBadge from '../components/VisionModeBadge';
import EquityCurve from '../components/EquityCurve';
import DrawdownChart from '../components/DrawdownChart';
import Tooltip from '../components/Tooltip';
import TradeChartModal from '../components/TradeChartModal';
import { formatForDisplay } from '../utils/timezone';

const STARTING_BALANCE = 100000;

function ModelDetail() {
  const { id } = useParams();
  const [model, setModel] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [trades, setTrades] = useState([]);
  const [decisions, setDecisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedTrade, setExpandedTrade] = useState(null);
  const [tradeReasoning, setTradeReasoning] = useState({});
  const [loadingReasoning, setLoadingReasoning] = useState({});
  const [chartModal, setChartModal] = useState({ isOpen: false, tradeId: null });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [modelRes, perfRes, tradesRes, decisionsRes] = await Promise.all([
          fetch(`/api/models/${id}`),
          fetch(`/api/models/${id}/performance`),
          fetch(`/api/models/${id}/trades?limit=10000`),
          fetch(`/api/models/${id}/decisions`)
        ]);

        if (modelRes.ok) setModel(await modelRes.json());
        if (perfRes.ok) setPerformance(await perfRes.json());
        if (tradesRes.ok) {
          const data = await tradesRes.json();
          setTrades(data.trades || []);
        }
        if (decisionsRes.ok) {
          const data = await decisionsRes.json();
          setDecisions(data.decisions || []);
        }
      } catch (err) {
        console.error('Error fetching model data:', err);
      }
      setLoading(false);
    };

    fetchData();
  }, [id]);

  // Calculate max drawdown from trades
  const maxDrawdown = useMemo(() => {
    if (!trades || trades.length === 0) return 0;

    const closedTrades = trades
      .filter(t => t.status === 'CLOSED' && t.exit_time && (t.pnl_usd ?? t.pnl_points) !== null)
      .sort((a, b) => new Date(a.exit_time) - new Date(b.exit_time));

    if (closedTrades.length === 0) return 0;

    let balance = STARTING_BALANCE;
    let peak = STARTING_BALANCE;
    let maxDD = 0;

    for (const trade of closedTrades) {
      balance += (trade.pnl_usd ?? trade.pnl_points);
      if (balance > peak) peak = balance;
      const drawdown = ((peak - balance) / peak) * 100;
      maxDD = Math.max(maxDD, drawdown);
    }

    return maxDD;
  }, [trades]);

  const handleViewReasoning = async (tradeId) => {
    if (expandedTrade === tradeId) {
      setExpandedTrade(null);
      return;
    }

    setExpandedTrade(tradeId);

    if (!tradeReasoning[tradeId]) {
      setLoadingReasoning(prev => ({ ...prev, [tradeId]: true }));
      try {
        const res = await fetch(`/api/trades/${tradeId}/reasoning`);
        if (res.ok) {
          const data = await res.json();
          setTradeReasoning(prev => ({ ...prev, [tradeId]: data }));
        }
      } catch (err) {
        console.error('Error fetching trade reasoning:', err);
      }
      setLoadingReasoning(prev => ({ ...prev, [tradeId]: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-primary"></div>
      </div>
    );
  }

  if (!model) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl text-gray-400">Model not found</h2>
        <Link to="/" className="text-accent-primary hover:underline mt-4 inline-block">
          Back to Dashboard
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center space-x-3 sm:space-x-4">
          <Link to="/" className="text-gray-400 hover:text-white text-sm sm:text-base">
            &larr; Back
          </Link>
          <div>
            <div className="flex items-center space-x-2 sm:space-x-3">
              <h1 className="text-xl sm:text-3xl font-bold">{model.name}</h1>
              <VisionModeBadge mode={model.vision_mode} />
            </div>
            <p className="text-gray-400 text-xs sm:text-base">{model.provider}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          {model.current_position && (
            <div className={`px-2 sm:px-4 py-1 sm:py-2 rounded-lg text-xs sm:text-base ${
              model.current_position.direction === 'LONG'
                ? 'bg-accent-success/20 text-accent-success'
                : 'bg-accent-danger/20 text-accent-danger'
            }`}>
              {model.current_position.direction} @ {model.current_position.entry_price}
            </div>
          )}
          {model.cooldown_status?.inCooldown && (
            <div className="px-2 sm:px-4 py-1 sm:py-2 rounded-lg bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 text-xs sm:text-base">
              <span className="font-semibold">COOLDOWN</span>
              <span className="text-xs sm:text-sm ml-1 sm:ml-2">{model.cooldown_status.remainingMinutes}min</span>
            </div>
          )}
        </div>
      </div>

      {/* Performance Metrics */}
      {performance && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 sm:gap-4">
          <MetricCard
            label="Total P/L"
            tooltip="Total profit or loss in USD"
            value={`$${performance.total_pnl >= 0 ? '+' : ''}${performance.total_pnl?.toFixed(2)}`}
            color={performance.total_pnl >= 0 ? 'text-accent-success' : 'text-accent-danger'}
          />
          <MetricCard
            label="Win Rate"
            tooltip="Percentage of profitable trades"
            value={`${performance.win_rate?.toFixed(1)}%`}
          />
          <MetricCard
            label="Profit Factor"
            tooltip="Ratio of gross profit to gross loss"
            value={performance.profit_factor?.toFixed(2)}
          />
          <MetricCard
            label="Trades"
            tooltip="Total number of completed trades"
            value={performance.trade_count}
          />
          <MetricCard
            label="Wins/Losses"
            tooltip="Number of winning trades vs losing trades"
            value={`${performance.wins}/${performance.losses}`}
          />
          <MetricCard
            label="Max Drawdown"
            tooltip="Maximum peak-to-trough decline in account balance"
            value={`${maxDrawdown.toFixed(2)}%`}
            color={maxDrawdown > 10 ? 'text-accent-danger' : maxDrawdown > 5 ? 'text-yellow-400' : 'text-gray-300'}
          />
        </div>
      )}

      {/* Equity Curve */}
      <EquityCurve trades={trades} />

      {/* Drawdown Chart */}
      <DrawdownChart trades={trades} />

      {/* Trade History */}
      <div className="bg-dark-surface rounded-lg overflow-hidden">
        <div className="px-3 sm:px-6 py-3 sm:py-4 border-b border-gray-700">
          <h2 className="text-base sm:text-xl font-semibold">Trade History</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-800">
              <tr>
                <th className="px-2 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-400 uppercase">Dir</th>
                <th className="px-2 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-400 uppercase">Entry</th>
                <th className="px-2 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-400 uppercase">Exit</th>
                <th className="hidden md:table-cell px-2 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-400 uppercase">SL/TP</th>
                <th className="hidden sm:table-cell px-2 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-400 uppercase">Exit Reason</th>
                <th className="px-2 sm:px-6 py-2 sm:py-3 text-right text-[10px] sm:text-xs font-medium text-gray-400 uppercase">P/L</th>
                <th className="px-2 sm:px-6 py-2 sm:py-3 text-center text-[10px] sm:text-xs font-medium text-gray-400 uppercase">Chart</th>
                <th className="px-2 sm:px-6 py-2 sm:py-3 text-center text-[10px] sm:text-xs font-medium text-gray-400 uppercase">View</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {trades.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-3 sm:px-6 py-6 sm:py-8 text-center text-gray-400 text-sm">
                    No trades yet
                  </td>
                </tr>
              ) : (
                trades.map((trade) => (
                  <React.Fragment key={trade.id}>
                    <tr className="hover:bg-gray-800/50">
                      <td className="px-2 sm:px-6 py-2 sm:py-4">
                        <span className={`px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-[10px] sm:text-xs font-medium ${
                          trade.direction === 'LONG'
                            ? 'bg-accent-success/20 text-accent-success'
                            : 'bg-accent-danger/20 text-accent-danger'
                        }`}>
                          {trade.direction}
                        </span>
                      </td>
                      <td className="px-2 sm:px-6 py-2 sm:py-4 font-mono text-xs sm:text-sm">
                        <div>{trade.entry_price?.toFixed(2)}</div>
                        <div className="text-[10px] sm:text-xs text-gray-400 hidden sm:block">{formatTime(trade.entry_time)}</div>
                      </td>
                      <td className="px-2 sm:px-6 py-2 sm:py-4 font-mono text-xs sm:text-sm">
                        {trade.exit_price ? (
                          <>
                            <div>{trade.exit_price?.toFixed(2)}</div>
                            <div className="text-[10px] sm:text-xs text-gray-400 hidden sm:block">{formatTime(trade.exit_time)}</div>
                          </>
                        ) : (
                          <span className="text-gray-400">Open</span>
                        )}
                      </td>
                      <td className="hidden md:table-cell px-2 sm:px-6 py-2 sm:py-4 font-mono text-xs sm:text-sm text-gray-400">
                        {trade.stop_loss && <div>SL: {trade.stop_loss?.toFixed(2)}</div>}
                        {trade.take_profit && <div>TP: {trade.take_profit?.toFixed(2)}</div>}
                      </td>
                      <td className="hidden sm:table-cell px-2 sm:px-6 py-2 sm:py-4 text-sm">
                        {trade.exit_reason && (
                          <span className={`px-2 py-1 rounded text-xs ${getExitReasonColor(trade.exit_reason)}`}>
                            {trade.exit_reason}
                          </span>
                        )}
                      </td>
                      <td className={`px-2 sm:px-6 py-2 sm:py-4 text-right font-mono text-xs sm:text-sm font-medium ${
                        ((trade.pnl_usd ?? trade.pnl_points) || 0) >= 0 ? 'text-accent-success' : 'text-accent-danger'
                      }`}>
                        {(trade.pnl_usd ?? trade.pnl_points) !== null ? (
                          `$${(trade.pnl_usd ?? trade.pnl_points) >= 0 ? '+' : ''}${(trade.pnl_usd ?? trade.pnl_points)?.toFixed(2)}`
                        ) : '-'}
                      </td>
                      <td className="px-2 sm:px-6 py-2 sm:py-4 text-center">
                        {trade.chart_path ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setChartModal({ isOpen: true, tradeId: trade.id });
                            }}
                            className="p-1.5 sm:p-2 rounded-lg text-gray-400 hover:text-accent-primary hover:bg-accent-primary/10 transition-all"
                            title="View trade chart"
                          >
                            <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                              <path strokeLinecap="round" strokeLinejoin="round"
                                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </button>
                        ) : (
                          <span className="text-gray-600">-</span>
                        )}
                      </td>
                      <td className="px-2 sm:px-6 py-2 sm:py-4 text-center">
                        <button
                          onClick={() => handleViewReasoning(trade.id)}
                          className={`p-1.5 sm:p-2 rounded-lg transition-all ${
                            expandedTrade === trade.id
                              ? 'bg-accent-primary/20 text-accent-primary'
                              : 'text-gray-400 hover:text-accent-primary hover:bg-accent-primary/10'
                          }`}
                          title="View trade reasoning"
                        >
                          <svg
                            className={`w-3 h-3 sm:w-4 sm:h-4 transition-transform duration-200 ${
                              expandedTrade === trade.id ? 'rotate-180' : ''
                            }`}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                    {/* Expanded Reasoning Row */}
                    <tr>
                      <td colSpan={8} className="p-0">
                        <div
                          className={`overflow-hidden transition-all duration-300 ease-in-out ${
                            expandedTrade === trade.id ? 'max-h-[3000px] opacity-100' : 'max-h-0 opacity-0'
                          }`}
                        >
                          <div className="px-6 py-4 bg-gray-800/30">
                            {/* Trade Chart */}
                            {trade.chart_path && expandedTrade === trade.id && (
                              <div className="mb-4 flex justify-center">
                                <img
                                  src={`/api/trades/${trade.id}/chart`}
                                  alt="Trade chart"
                                  className="max-w-full h-auto rounded-lg border border-gray-700 cursor-pointer hover:opacity-90 transition-opacity"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setChartModal({ isOpen: true, tradeId: trade.id });
                                  }}
                                />
                              </div>
                            )}
                            <TradeReasoningPanel
                              reasoning={tradeReasoning[trade.id]}
                              loading={loadingReasoning[trade.id]}
                              trade={trade}
                            />
                          </div>
                        </div>
                      </td>
                    </tr>
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Decision Log */}
      <div className="bg-dark-surface rounded-lg overflow-hidden">
        <div className="px-3 sm:px-6 py-3 sm:py-4 border-b border-gray-700">
          <h2 className="text-base sm:text-xl font-semibold">Decision Log</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-800">
              <tr>
                <th className="px-2 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-400 uppercase">Time</th>
                <th className="px-2 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-400 uppercase">Action</th>
                <th className="hidden sm:table-cell px-2 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-400 uppercase">Conf</th>
                <th className="hidden md:table-cell px-2 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-400 uppercase">Mode</th>
                <th className="hidden lg:table-cell px-2 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-400 uppercase">Reasoning</th>
                <th className="hidden md:table-cell px-2 sm:px-6 py-2 sm:py-3 text-right text-[10px] sm:text-xs font-medium text-gray-400 uppercase">Latency</th>
                <th className="px-2 sm:px-6 py-2 sm:py-3 text-right text-[10px] sm:text-xs font-medium text-gray-400 uppercase">Cost</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {decisions.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-3 sm:px-6 py-6 sm:py-8 text-center text-gray-400 text-sm">
                    No decisions yet
                  </td>
                </tr>
              ) : (
                decisions.map((decision) => (
                  <tr key={decision.id} className="hover:bg-gray-800/50">
                    <td className="px-2 sm:px-6 py-2 sm:py-4 text-[10px] sm:text-sm text-gray-300">
                      {formatTime(decision.timestamp)}
                    </td>
                    <td className="px-2 sm:px-6 py-2 sm:py-4">
                      <span className={`px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-[10px] sm:text-xs font-medium ${getActionColor(decision.action)}`}>
                        {decision.action}
                      </span>
                    </td>
                    <td className="hidden sm:table-cell px-2 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm">
                      {decision.confidence !== null ? (
                        <span className={`font-medium ${
                          decision.confidence >= 70 ? 'text-accent-success' :
                          decision.confidence >= 40 ? 'text-yellow-400' : 'text-gray-400'
                        }`}>
                          {decision.confidence}%
                        </span>
                      ) : '-'}
                    </td>
                    <td className="hidden md:table-cell px-2 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm text-gray-400">
                      {decision.vision_mode}
                    </td>
                    <td className="hidden lg:table-cell px-2 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm text-gray-300 max-w-xs truncate">
                      {decision.reasoning || '-'}
                    </td>
                    <td className="hidden md:table-cell px-2 sm:px-6 py-2 sm:py-4 text-right text-xs sm:text-sm font-mono text-gray-400">
                      {decision.latency_ms}ms
                    </td>
                    <td className="px-2 sm:px-6 py-2 sm:py-4 text-right text-[10px] sm:text-sm font-mono text-gray-400">
                      ${decision.cost_usd?.toFixed(4)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Trade Chart Modal */}
      <TradeChartModal
        isOpen={chartModal.isOpen}
        onClose={() => setChartModal({ isOpen: false, tradeId: null })}
        tradeId={chartModal.tradeId}
      />
    </div>
  );
}

function getActionColor(action) {
  switch (action) {
    case 'LONG':
      return 'bg-accent-success/20 text-accent-success';
    case 'SHORT':
      return 'bg-accent-danger/20 text-accent-danger';
    case 'FLAT':
      return 'bg-gray-500/20 text-gray-400';
    case 'HOLD':
      return 'bg-accent-primary/20 text-accent-primary';
    default:
      return 'bg-gray-500/20 text-gray-400';
  }
}

function MetricCard({ label, value, color = 'text-dark-text', tooltip }) {
  return (
    <Tooltip text={tooltip}>
      <div className="bg-dark-surface rounded-lg p-2 sm:p-4 cursor-help">
        <div className="text-xs sm:text-sm text-gray-400">{label}</div>
        <div className={`text-base sm:text-2xl font-bold ${color}`}>{value}</div>
      </div>
    </Tooltip>
  );
}

function formatTime(isoString) {
  if (!isoString) return '';
  // Use UTC for display
  return formatForDisplay(isoString, {
    includeDate: true,
    includeTime: true,
    includeTimezone: true,
    shortDate: true
  });
}

function getExitReasonColor(reason) {
  switch (reason) {
    case 'TP_HIT':
      return 'bg-accent-success/20 text-accent-success';
    case 'SL_HIT':
      return 'bg-accent-danger/20 text-accent-danger';
    case 'LLM_CLOSE':
      return 'bg-accent-primary/20 text-accent-primary';
    case 'SESSION_END':
      return 'bg-gray-500/20 text-gray-400';
    case 'MANUAL':
      return 'bg-accent-purple/20 text-accent-purple';
    default:
      return 'bg-gray-500/20 text-gray-400';
  }
}

function TradeReasoningPanel({ reasoning, loading, trade }) {
  if (loading) {
    return (
      <div className="bg-[#1E293B] rounded-lg p-3 sm:p-4 animate-pulse">
        <div className="h-4 bg-gray-700 rounded w-1/4 mb-3"></div>
        <div className="h-3 bg-gray-700 rounded w-full mb-2"></div>
        <div className="h-3 bg-gray-700 rounded w-3/4"></div>
      </div>
    );
  }

  const hasEntryReasoning = reasoning?.entry?.trade_reasoning ||
                            reasoning?.entry?.sl_reasoning ||
                            reasoning?.entry?.tp_reasoning;

  if (!hasEntryReasoning) {
    return (
      <div className="bg-[#1E293B] rounded-lg p-3 sm:p-4 text-center text-gray-400 text-xs sm:text-sm">
        Reasoning not available for this trade
      </div>
    );
  }

  return (
    <div className="bg-[#1E293B] rounded-lg p-3 sm:p-4">
      <h4 className="text-[10px] sm:text-xs font-semibold text-accent-primary uppercase tracking-wider mb-2 sm:mb-3 flex items-center gap-1.5 sm:gap-2">
        <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14" />
        </svg>
        Entry Decision
      </h4>
      <div className="space-y-2 sm:space-y-3">
        {reasoning.entry.trade_reasoning && (
          <div>
            <h5 className="text-[10px] sm:text-xs font-medium text-gray-400 mb-0.5 sm:mb-1">Trade Rationale</h5>
            <p className="text-xs sm:text-sm text-[#F1F5F9] leading-relaxed">
              {reasoning.entry.trade_reasoning}
            </p>
          </div>
        )}
        {reasoning.entry.sl_reasoning && (
          <div>
            <h5 className="text-[10px] sm:text-xs font-medium text-accent-danger mb-0.5 sm:mb-1">Stop Loss Reasoning</h5>
            <p className="text-xs sm:text-sm text-[#F1F5F9] leading-relaxed">
              {reasoning.entry.sl_reasoning}
            </p>
          </div>
        )}
        {reasoning.entry.tp_reasoning && (
          <div>
            <h5 className="text-[10px] sm:text-xs font-medium text-accent-success mb-0.5 sm:mb-1">Take Profit Reasoning</h5>
            <p className="text-xs sm:text-sm text-[#F1F5F9] leading-relaxed">
              {reasoning.entry.tp_reasoning}
            </p>
          </div>
        )}
        {!reasoning.entry.trade_reasoning && reasoning.entry.reasoning && (
          <div>
            <h5 className="text-[10px] sm:text-xs font-medium text-gray-400 mb-0.5 sm:mb-1">Reason</h5>
            <p className="text-xs sm:text-sm text-[#F1F5F9] leading-relaxed">
              {reasoning.entry.reasoning}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default ModelDetail;
